import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import { doc, updateDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { useAuth, UserRole } from '../context/AuthContext';

const ROLE_COLORS: Record<UserRole, 'error' | 'warning' | 'default'> = {
  admin: 'error',
  author: 'warning',
  member: 'default',
};

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' };

export default function MyAccountPage() {
  const { authUser, currentUser } = useAuth();

  const [name, setName] = React.useState(currentUser?.name ?? '');
  const [bio, setBio] = React.useState(currentUser?.bio ?? '');
  const [saving, setSaving] = React.useState(false);
  const [snack, setSnack] = React.useState<SnackState>({ open: false, message: '', severity: 'success' });

  // Sync fields if currentUser loads after mount
  React.useEffect(() => {
    if (currentUser) {
      setName(currentUser.name);
      setBio(currentUser.bio ?? '');
    }
  }, [currentUser]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user || !authUser) return;

    const trimmedName = name.trim();
    if (trimmedName.length < 3) {
      setSnack({ open: true, message: 'Screen name must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedName)) {
      setSnack({ open: true, message: 'Only letters, numbers, and underscores allowed in screen name.', severity: 'error' });
      return;
    }
    if (bio.length > 160) {
      setSnack({ open: true, message: 'Bio must be 160 characters or fewer.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      await updateProfile(user, { displayName: trimmedName });
      await updateDoc(doc(db, 'users', user.uid), { name: trimmedName, bio });
      setSnack({ open: true, message: 'Profile updated.', severity: 'success' });
    } catch (err) {
      console.error('Profile update failed:', err);
      setSnack({ open: true, message: 'Failed to save changes. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
        My Account
      </Typography>

      <Box component="form" onSubmit={handleSave} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Avatar + identity summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 80, height: 80, fontSize: '2rem' }}>
            {currentUser.name[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {currentUser.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentUser.email}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={currentUser.role ?? 'no role'}
                color={ROLE_COLORS[currentUser.role] ?? 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Editable fields */}
        <FormControl>
          <FormLabel htmlFor="name">Screen name</FormLabel>
          <TextField
            id="name"
            value={name}
            required
            onChange={e => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 40 } }}
            helperText="Letters, numbers, and underscores only."
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="bio">Bio</FormLabel>
          <TextField
            id="bio"
            value={bio}
            multiline
            rows={4}
            variant="standard"
            onChange={e => setBio(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 160 } }}
            helperText={`${bio.length}/160`}
          />
        </FormControl>

        <Divider />

        {/* Read-only info */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Email (cannot be changed)
          </Typography>
          <Typography variant="body2">{currentUser.email}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Account type
          </Typography>
          <Typography variant="body2">{currentUser.type}</Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={{ alignSelf: 'flex-start' }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
